import isEmpty from 'lodash/isEmpty'
import httpStatuses from '@app/httpStatusCodes'
import { contextOriginService } from '@app/auth/contextOriginService'
import { HEADER_REGGORA_ORIGIN } from '@config/headers'
import authLogger from './auth-logger'

/**
 * Build a validated URL from base, path, and optional query parameters.
 * @param {string} baseUrl - The base URL (e.g. 'https://api.example.com')
 * @param {string} path - The endpoint path (e.g. '/lender/auth/login')
 * @param {Object} [queryParams={}] - Key-value pairs to serialize as query string
 * @returns {string} The full URL
 */
function buildValidatedUrl(baseUrl, path, queryParams = {}) {
  try {
    // Minimal path validation (Do this before new URL(baseUrl), as URL() resolves dot-segments.)
    if (baseUrl.includes('/../') || /\/%2e%2e\//i.test(baseUrl)) {
      throw new Error('Invalid path')
    }
    if (path.includes('/../') || /\/%2e%2e\//i.test(path)) {
      throw new Error('Invalid path')
    }

    const url = new URL(baseUrl)

    // Protocol checks
    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error('Invalid protocol')
    }

    // Validate path parameter
    if (path && !/^\/[A-Za-z0-9_\-/]*$/.test(path)) {
      throw new Error('Invalid parameter')
    }

    // Build pathname from base and validated path
    if (path) {
      url.pathname = url.pathname.replace(/\/+$/, '') + path
    }

    // Add query parameters
    if (!isEmpty(queryParams)) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, value)
        }
      })
    }

    return url.href
  } catch {
    throw new Error('Invalid URL')
  }
}

/**
 * Build a full URL from base, path, and optional query parameters.
 * @param {string} base - The base URL (e.g. 'https://api.example.com')
 * @param {string} path - The endpoint path (e.g. '/lender/auth/login')
 * @param {Object} [queryParams={}] - Key-value pairs to serialize as query string
 * @returns {string} The full URL
 */
function buildUrl(base, path, queryParams = {}) {
  return buildValidatedUrl(base, path, queryParams)
}

/**
 * Build a headers object containing default headers for all auth API requests.
 * @returns {Object} Headers including the Reggora origin context if available
 */
function defaultHeaders() {
  const headers = {}
  const origin = contextOriginService.getToken()
  if (origin) {
    headers[HEADER_REGGORA_ORIGIN] = origin
  }
  return headers
}

const SLOW_REQUEST_THRESHOLD_MS = 2000

/**
 * Execute a fetch Request with response parsing, error handling, and timing.
 *
 * - JSON responses are parsed automatically; non-JSON returns the raw text.
 * - Non-OK responses throw an Error with `.status` and `.data` properties.
 * - Network failures and timeouts throw an Error with `.status = 0`.
 * - Requests exceeding {@link SLOW_REQUEST_THRESHOLD_MS} are logged as warnings.
 *
 * @param {Request} request - A fully constructed Request object
 * @returns {Promise<any>} The parsed response body
 * @throws {Error} On HTTP errors, network failures, or timeouts
 */
async function authFetch(request) {
  const start = performance.now()
  const method = request.method
  const url = request.url

  try {
    const response = await fetch(request)

    const text = await response.text()
    let body
    if (text) {
      try {
        body = JSON.parse(text)
      } catch {
        body = text
      }
    }

    const durationMs = Math.round(performance.now() - start)

    if (durationMs >= SLOW_REQUEST_THRESHOLD_MS) {
      authLogger.warn('Slow API request:', { method, url, status: response.status, durationMs })
    }

    if (response.ok) {
      return body
    }

    const error = new Error(body?.detail || body?.error || `Request failed with status ${response.status}`)
    error.status = response.status
    error.data = body
    throw error
  } catch (error) {
    const durationMs = Math.round(performance.now() - start)

    if (error.status) {
      authLogger.error('API response error:', { method, url, status: error.status, durationMs }, error)
      throw error
    }

    const isTimeout = error.name === 'TimeoutError' || /timeout|aborted/i.test(error.message)
    const message = isTimeout ? `Request timed out: ${url}` : `Network error: ${url}`
    authLogger.warn(message, { method, url, durationMs })
    const wrapped = new Error(message)
    wrapped.status = 0
    throw wrapped
  }
}

/**
 * HTTP client for authenticated API requests.
 * Wraps native fetch with default headers, response parsing, and error handling.
 * Methods accept standard {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request RequestInit} options.
 */
export class AuthApiClient {
  /**
   * @param {string} baseUrl - The base URL for all requests (trailing slashes are stripped)
   */
  constructor(baseUrl) {
    this.baseUrl = baseUrl.replace(/\/+$/, '')
  }

  /**
   * Send a GET request.
   * @param {string} path - The endpoint path
   * @param {Object} [queryParams={}] - Query parameters to serialize
   * @param {RequestInit} [init={}] - Additional Request options (e.g. headers, credentials, signal)
   * @returns {Promise<any>} The parsed response body
   */
  get(path, queryParams = {}, init = {}) {
    const request = new Request(buildUrl(this.baseUrl, path, queryParams), {
      ...init,
      headers: { ...defaultHeaders(), ...init.headers },
    })
    return authFetch(request)
  }

  /**
   * Send a POST request with a JSON body.
   * @param {string} path - The endpoint path
   * @param {Object} [queryParams={}] - Query parameters to serialize
   * @param {Object} [body={}] - The request body (will be JSON-serialized)
   * @param {RequestInit} [init={}] - Additional Request options (e.g. headers, credentials, signal)
   * @returns {Promise<any>} The parsed response body
   */
  post(path, queryParams = {}, body = {}, init = {}) {
    const request = new Request(buildUrl(this.baseUrl, path, queryParams), {
      ...init,
      method: 'POST',
      headers: {
        ...defaultHeaders(),
        'Content-Type': 'application/json',
        ...init.headers,
      },
      body: JSON.stringify(body),
    })
    return authFetch(request)
  }

  /**
   * Send a POST request with a FormData body (multipart/form-data).
   * Content-Type is set automatically by the browser to include the boundary.
   * @param {string} path - The endpoint path
   * @param {Object} [queryParams={}] - Query parameters to serialize
   * @param {FormData} formData - The form data to send
   * @param {RequestInit} [init={}] - Additional Request options (e.g. headers, credentials, signal)
   * @returns {Promise<any>} The parsed response body
   */
  postForm(path, queryParams = {}, formData, init = {}) {
    const request = new Request(buildUrl(this.baseUrl, path, queryParams), {
      ...init,
      method: 'POST',
      headers: { ...defaultHeaders(), ...init.headers },
      body: formData,
    })
    return authFetch(request)
  }
}

export const statusCodes = httpStatuses

/**
 * Create a new AuthApiClient instance.
 * @param {string} baseUrl - The base URL for the API
 * @returns {AuthApiClient}
 */
export default function(baseUrl) {
  return new AuthApiClient(baseUrl)
}
